import { QueryClient, QueryFunction } from "@tanstack/react-query";
import { getAuthToken } from "./authToken";

async function throwIfResNotOk(res: Response) {
  if (!res.ok) {
    let message = res.statusText;
    try {
      const data = await res.json();
      if (data && typeof data === 'object' && data.message) {
        message = data.message;
      } else if (typeof data === 'string') {
        message = data;
      }
    } catch {
      // Not JSON, fallback to statusText or body text
      try {
        const text = await res.text();
        if (text) message = text;
      } catch {
        // Ignore
      }
    }

    const error = new Error(message) as Error & { status: number };
    error.status = res.status;
    throw error;
  }
}

/**
 * Build headers for API requests, including the Firebase ID token
 * for Safari compatibility (Safari blocks SameSite=None cookies).
 */
async function buildAuthHeaders(additionalHeaders?: Record<string, string>): Promise<Record<string, string>> {
  const headers: Record<string, string> = { ...additionalHeaders };

  try {
    const token = await getAuthToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
  } catch (error) {
    // Token fetch failed - continue without it, session cookie may still work
    console.warn('[queryClient] Failed to get auth token:', error);
  }

  return headers;
}

export async function apiRequest(
  method: string,
  url: string,
  data?: unknown | undefined,
): Promise<Response> {
  // Prepend API base URL if it's a relative path
  const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || '';
  const fullUrl = url.startsWith('http') ? url : `${apiBaseUrl}${url}`;

  // Build headers with auth token and optional Content-Type
  const baseHeaders: Record<string, string> = data ? { "Content-Type": "application/json" } : {};
  const headers = await buildAuthHeaders(baseHeaders);

  const res = await fetch(fullUrl, {
    method,
    headers,
    body: data ? JSON.stringify(data) : undefined,
    credentials: "include",
  });

  await throwIfResNotOk(res);
  return res;
}

type UnauthorizedBehavior = "returnNull" | "throw";
export const getQueryFn: <T>(options: {
  on401: UnauthorizedBehavior;
}) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior }) =>
    async ({ queryKey }) => {
      const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || '';
      const url = queryKey.join("/") as string;
      const fullUrl = url.startsWith('http') ? url : `${apiBaseUrl}${url}`;

      // Build headers with auth token for Safari compatibility
      const headers = await buildAuthHeaders();

      const res = await fetch(fullUrl, {
        headers,
        credentials: "include",
      });

      if (unauthorizedBehavior === "returnNull" && res.status === 401) {
        return null;
      }

      await throwIfResNotOk(res);
      return await res.json();
    };

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "throw" }),
      refetchInterval: false,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
      retry: false,
    },
    mutations: {
      retry: false,
    },
  },
});
